import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../css/auth/Auth.css';
import TabBar from '../shared/TabBar';
import MenuScreen from '../menu/MenuScreen';
import SupportChat from '../shared/SupportChat';
import { useLang } from '../../i18n';
import { useGeo } from '../../geo';

function useIsDesktop() {
  const [desktop, setDesktop] = useState(() => window.innerWidth >= 1023);
  useEffect(() => {
    const h = () => setDesktop(window.innerWidth >= 1023);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return desktop;
}

const API = process.env.REACT_APP_API_BASE_URL;

/* ─── Icons ─────────────────────────────────────────────────────────────── */
function MailIcon() {
  return (
    <svg className="fic" viewBox="0 0 18 18" fill="none">
      <rect x="1.5" y="3.5" width="15" height="11" rx="2.5" stroke="#757490" strokeWidth="1.3" />
      <path d="M2.5 5l6.5 4.5L15.5 5" stroke="#757490" strokeWidth="1.3" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="fic" viewBox="0 0 18 18" fill="none">
      <rect x="3.5" y="8" width="11" height="7.5" rx="2" stroke="#757490" strokeWidth="1.3" />
      <path d="M6 8V6a3 3 0 016 0v2" stroke="#757490" strokeWidth="1.3" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg className="fic" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="6" r="3" stroke="#757490" strokeWidth="1.3" />
      <path d="M3.5 15.5a5.5 5.5 0 0111 0" stroke="#757490" strokeWidth="1.3" />
    </svg>
  );
}

function Eye({ off }) {
  return (
    <span className="auth-eye">
      <svg viewBox="0 0 20 20" fill="none">
        <path d="M1.5 10S4.5 4.5 10 4.5 18.5 10 18.5 10 15.5 15.5 10 15.5 1.5 10 1.5 10z" stroke="#757490" strokeWidth="1.3" />
        <circle cx="10" cy="10" r="2.5" stroke="#757490" strokeWidth="1.3" />
        {off ? <path d="M3 3l14 14" stroke="#757490" strokeWidth="1.3" strokeLinecap="round" /> : null}
      </svg>
    </span>
  );
}

function Field({ label, icon, type, placeholder, name, value, onChange }) {
  const [show, setShow] = useState(false);
  const isPw = type === 'password';
  return (
    <div className="auth-field">
      <div className="auth-label">{label}</div>
      <label className="auth-input">
        {icon}
        <input
          type={isPw && !show ? 'password' : 'text'}
          placeholder={placeholder}
          name={name}
          value={value}
          onChange={onChange}
          autoComplete={isPw ? 'current-password' : 'off'}
        />
        {isPw ? (
          <button type="button" onClick={() => setShow(!show)}>
            <Eye off={show} />
          </button>
        ) : null}
      </label>
    </div>
  );
}

function Gift({ id, box1, box2, bow1, bow2, coin }) {
  return (
    <svg width="62" height="56" viewBox="0 0 62 56">
      <defs>
        <linearGradient id={id + 'b'} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={box1} /><stop offset="1" stopColor={box2} />
        </linearGradient>
        <linearGradient id={id + 'r'} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={bow1} /><stop offset="1" stopColor={bow2} />
        </linearGradient>
      </defs>
      <ellipse cx="31" cy="52" rx="20" ry="3.2" fill="rgba(0,0,0,.35)" />
      <rect x="10" y="25" width="42" height="26" rx="4" fill={'url(#' + id + 'b)'} />
      <rect x="7" y="18" width="48" height="10" rx="3" fill={'url(#' + id + 'b)'} opacity="0.9" />
      <rect x="26" y="18" width="10" height="33" fill={'url(#' + id + 'r)'} />
      <path d="M31 18 C 20 5, 9 11, 31 18 Z" fill={'url(#' + id + 'r)'} />
      <path d="M31 18 C 42 5, 53 11, 31 18 Z" fill={'url(#' + id + 'r)'} />
      <circle cx="31" cy="17" r="3.4" fill={bow1} />
      {coin ? <circle cx="46" cy="41" r="8.5" fill="#f7c453" stroke="#ffe08a" strokeWidth="1.5" /> : null}
      {coin ? <text x="46" y="44.5" textAnchor="middle" fontSize="9" fontWeight="800" fill="#6b4a00">€</text> : null}
    </svg>
  );
}

function Rewards() {
  const { t } = useLang();
  const { depositBonusCap, symbol } = useGeo().prices;
  return (
    <div className="auth-rewards">
      <div className="reward-card on" style={{ pointerEvents: 'none' }}>
        <div className="reward-art"><Gift id="gd" box1="#ffe39a" box2="#f0a93b" bow1="#76e1ff" bow2="#8187f6" /></div>
        <div className="reward-text">
          <div className="reward-title">{t('auth.doubleDeposit')}</div>
          <p className="reward-sub">{t('auth.doubleDepositSub', { cap: `${depositBonusCap} ${symbol}` })}</p>
        </div>
        <span className="reward-badge">×3</span>
      </div>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────── */
export default function AuthForm() {
  const { t } = useLang();
  const { geo } = useGeo() || {};
  const isDesktop = useIsDesktop();
  const initTab = new URLSearchParams(window.location.search).get('tab') === 'signup' ? 'signup' : 'signin';
  const [tab, setTab]         = useState(initTab);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [acceptTerms, setAcceptTerms] = useState(true);

  const navigate  = useNavigate();
  const location  = useLocation();
  const redirectTo = new URLSearchParams(location.search).get('redirect') || '/';

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    username: '', nom: '', prenom: '', email: '', password: ''
  });

  const handleLoginChange = (e) => {
    setLoginForm(p => ({ ...p, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleRegisterChange = (e) => {
    setRegisterForm(p => ({ ...p, [e.target.name]: e.target.value }));
    setError('');
  };

  /* ── Login ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/api/auth/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm),
      });
      const data = await res.json();
      if (data.success) {
        if (data.token) localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.data));
        navigate(redirectTo);
      } else {
        setError(data.message || t('auth.invalidCredentials'));
      }
    } catch {
      setError(t('common.serverError'));
    } finally {
      setLoading(false);
    }
  };

  /* ── Register ── */
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!acceptTerms) { setError(t('auth.mustAcceptTerms')); return; }
    setLoading(true);
    setError('');

    // Logs détaillés
    console.log('=== DÉBUT INSCRIPTION ===');
    console.log('Données formulaire:', registerForm);
    console.log('Pays:', geo?.country || 'non disponible');
    console.log('URL API:', `${API}/api/auth/register.php`);

    try {
      const requestBody = {
        ...registerForm,
        country: geo?.country || 'FR',
        ref_code: localStorage.getItem('ref_code') || undefined,
      };

      const res = await fetch(`${API}/api/auth/register.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const text = await res.text();

      let data;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        setError(t('auth.invalidServerResponse') + ' (format JSON invalide)');
        setLoading(false);
        return;
      }

      if (data.success) {
        const loginRes = await fetch(`${API}/api/auth/login.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: registerForm.email, password: registerForm.password }),
        });

        const loginData = await loginRes.json();

        if (loginData.success) {
          if (loginData.token) localStorage.setItem('token', loginData.token);
          localStorage.setItem('user', JSON.stringify(loginData.data));
          navigate('/');
        } else {
          navigate('/auth');
        }
      } else {
        setError(data.message || t('auth.accountCreationError'));
      }
    } catch (error) {
      setError(t('common.serverError'));
    } finally {
      setLoading(false);
    }
  };

  /* ── Contenu du formulaire (partagé mobile + desktop) ── */
  const formContent = (
    <div className="auth">
      <div className="auth-top">
        <button className="auth-close" onClick={() => navigate('/')} aria-label="Close">
          <svg width="13" height="13" viewBox="0 0 13 13">
            <path d="M2 2l9 9M11 2L2 11" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
        <span />
      </div>

      <div className="auth-logo">
        <img className="auth-logo-img" src="/logo.png" alt="Roxs Sports" />
      </div>

      <p className="auth-welcome">
        {tab === 'signin' ? t('auth.welcomeBack') : t('auth.createAccount')}
      </p>

      <div className="auth-tabs">
        <button className={'auth-tab' + (tab === 'signin' ? ' is-active' : '')} onClick={() => { setTab('signin'); setError(''); }}>{t('auth.login')}</button>
        <button className={'auth-tab' + (tab === 'signup' ? ' is-active' : '')} onClick={() => { setTab('signup'); setError(''); }}>{t('auth.register')}</button>
      </div>

      {error && <div className="auth-error">{error}</div>}

      {tab === 'signin' ? (
        <form onSubmit={handleLogin}>
          <Field label={t('auth.email')} icon={<MailIcon />} type="text" placeholder={t('auth.emailPlaceholder')} name="email" value={loginForm.email} onChange={handleLoginChange} />
          <Field label={t('auth.password')} icon={<LockIcon />} type="password" placeholder={t('auth.passwordPlaceholder')} name="password" value={loginForm.password} onChange={handleLoginChange} />
          <button className="auth-submit" type="submit" disabled={loading}>
            {loading ? t('auth.loggingIn') : t('auth.signIn')}
          </button>
        </form>
      ) : (
        <form onSubmit={handleRegister}>
          <Rewards />
          <Field label={t('auth.username')} icon={<UserIcon />} type="text" placeholder={t('auth.usernamePlaceholder')} name="username" value={registerForm.username} onChange={handleRegisterChange} />
          <div className="auth-field-row">
            <Field label={t('auth.lastName')}  icon={<UserIcon />} type="text" placeholder={t('auth.lastNamePlaceholder')}  name="nom"   value={registerForm.nom}   onChange={handleRegisterChange} />
            <Field label={t('auth.firstName')} icon={<UserIcon />} type="text" placeholder={t('auth.firstNamePlaceholder')} name="prenom" value={registerForm.prenom} onChange={handleRegisterChange} />
          </div>
          <Field label={t('auth.email')} icon={<MailIcon />} type="text" placeholder={t('auth.emailPlaceholder')} name="email" value={registerForm.email} onChange={handleRegisterChange} />
          <Field label={t('auth.password')} icon={<LockIcon />} type="password" placeholder={t('auth.createPassword')} name="password" value={registerForm.password} onChange={handleRegisterChange} />
          <div className="auth-meta">
            <span className="auth-check">
              <span
                className={'auth-box' + (acceptTerms ? ' on' : '')}
                onClick={() => setAcceptTerms(!acceptTerms)}
              >
                {acceptTerms ? (
                  <svg width="11" height="9" viewBox="0 0 11 9">
                    <path d="M1 4.5L4 7.5 10 1" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : null}
              </span>
              {t('auth.termsAccept')} <a href="/terms">{t('auth.terms')}</a>
            </span>
          </div>
          <button className="auth-submit" type="submit" disabled={loading || !acceptTerms}>
            {loading ? t('auth.creating') : t('auth.createMyAccount')}
          </button>
        </form>
      )}

      <p className="auth-foot">
        {tab === 'signin' ? (
          <>{t('auth.noAccount')} <a href="#!" onClick={(e) => { e.preventDefault(); setTab('signup'); setError(''); }}>{t('auth.signUp')}</a></>
        ) : (
          <>{t('auth.alreadyAccount')} <a href="#!" onClick={(e) => { e.preventDefault(); setTab('signin'); setError(''); }}>{t('auth.signIn')}</a></>
        )}
      </p>
    </div>
  );

  /* ── Layout desktop — split gauche/droite ── */
  if (isDesktop) {
    return (
      <div className="auth-dsk-stage">

        {/* Panneau gauche — branding */}
        <div className="auth-dsk-left">
          <div className="auth-dsk-left-inner">

            {/* Logo */}
            <div className="auth-dsk-brand" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              <img className="auth-dsk-img" src="/logo.png" alt="Roxs Sports" />
            </div>

            {/* Tagline */}
            <div className="auth-dsk-tagline">
              <h1>{t('auth.tagline').split('\n').map((l, i) => <span key={i}>{l}<br /></span>)}</h1>
              <p>{t('auth.taglineSub')}</p>
            </div>

            {/* Badges */}
            <div className="auth-dsk-badges">
              <div className="auth-dsk-badge">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    stroke="#76e1ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>{t('auth.secure')}</span>
              </div>
              <div className="auth-dsk-badge">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="#76e1ff" strokeWidth="1.5" />
                  <path d="M12 3a9 9 0 010 18M12 3a9 9 0 000 18M3 12h18" stroke="#76e1ff" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span>{t('auth.sportsCount')}</span>
              </div>
              <div className="auth-dsk-badge">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M13 2L4.09 12.26a1 1 0 00.82 1.61H11l-1 8 8.91-10.26a1 1 0 00-.82-1.61H13l1-8z"
                    stroke="#76e1ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>{t('auth.fastWithdrawals')}</span>
              </div>
            </div>

          </div>

          {/* Décoration fond */}
          <div className="auth-dsk-glow1" />
          <div className="auth-dsk-glow2" />
        </div>

        {/* Panneau droit — formulaire */}
        <div className="auth-dsk-right">
          <div className="auth-dsk-form-wrap">
            {formContent}
          </div>
        </div>

        <SupportChat />
      </div>
    );
  }

  /* ── Layout mobile (inchangé) ── */
  return (
    <div className="stage">
      <div className="phone">
        <main className="phone-screen">
          {formContent}
        </main>
        <TabBar active="" onMenu={() => setMenuOpen(true)} />
        <MenuScreen open={menuOpen} onClose={() => setMenuOpen(false)} />
        <SupportChat />
      </div>
    </div>
  );
}
